import * as React from 'react';
import { Placement } from '@floating-ui/react';
import { Text } from '../Typography';
import { TextProps } from '../Typography/components/Text/Text';
import { Inline } from '../../layout';
import { Button } from '../Button';
import { Icon, IconList } from '../Icon';
import { IconType } from '../Icon/Icon';
import { ToggletipTrigger, ToggletipContent } from './components';
import { useToggletip } from './hooks/useToggletip/useToggletip';

type ContextType = ReturnType<typeof useToggletip> | null;

const ToggletipContext = React.createContext<ContextType>(null);

export const useToggletipContext = () => {
  const context = React.useContext(ToggletipContext);

  if (context == null) {
    throw new Error('Toggletip components must be wrapped in <Toggletip />');
  }

  return context;
};

export type ToggletipProps = {
  idPrefix: string;
  contentClassName?: string;
  triggerText?: string;
  triggerVariant?: TextProps['variant'];
  ariaLabel?: string;
  icon?: IconType;
};

export type ToggletipVariant = 'light' | 'dark';

export type ToggletipOptions = {
  placement?: Placement;
  variant?: ToggletipVariant;
};

export function Toggletip({
  children,
  idPrefix,
  contentClassName,
  triggerText,
  triggerVariant,
  ariaLabel,
  placement,
  icon,
  variant = 'light',
}: { children: React.ReactNode } & ToggletipOptions &
  ToggletipProps): React.ReactElement {
  const toggletip = useToggletip({ placement, variant });
  return (
    <ToggletipContext.Provider value={toggletip}>
      <Inline spacing={2}>
        {triggerText && (
          <Text size="sm" variant={triggerVariant}>
            {triggerText}
          </Text>
        )}
        <ToggletipTrigger>
          <Button
            iconOnly={true}
            variant="ghost"
            size="xs"
            aria-describedby={idPrefix + '-content'}
            aria-controls={idPrefix + '-content'}
            aria-label={ariaLabel || 'More information about ' + triggerText}
            aria-pressed={toggletip.open}
          >
            <Icon icon={icon || IconList.light.faCircleQuestion} size="md" />
          </Button>
        </ToggletipTrigger>
        <ToggletipContent
          className={contentClassName}
          id={idPrefix + '-content'}
        >
          {children}
        </ToggletipContent>
      </Inline>
    </ToggletipContext.Provider>
  );
}
